import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import '@moods/ui/styles.css';
import './styles/app.css';
import './styles/shell.css';
import './styles/community.css';
import './styles/community-settings.css';
import './styles/moderation.css';
import './styles/safety.css';
import './styles/user-settings.css';
import './styles/responsive.css';
import './styles/mobile-pages.css';
import { loadUiLocale } from '@moods/ui';
import i18n, { i18nReady, withNamespaces } from './i18n';
import { Providers } from './app/Providers';
import { preloadRoute, router } from './app/router';
import { useAuth } from './store/auth';
import { restoreAppearance } from './features/settings/apply';
import { IS_APP_SHELL, IS_DESKTOP, IS_IOS_APP } from './lib/env';
import { installViewportVars } from './lib/viewport';
import { registerServiceWorker } from './lib/sw';
import { catchEarlyErrors, installMonitoring } from './lib/monitoring';
import { installStaleChunkReload } from './lib/stale-chunks';

// error tracking (Sentry, when the server has a web DSN): uncaught errors from here on are kept until it is ready
catchEarlyErrors();
// a chunk retired by a deploy: reload once onto the current build instead of an error screen
if (!IS_APP_SHELL) installStaleChunkReload();

const DesktopFrame = lazy(withNamespaces(() => import('./features/desktop/DesktopFrame').then(m => ({ default: m.DesktopFrame })), ['desktop']));

// zoom / density / saturation from the last session paint before READY (User Settings › Görünüm)
restoreAppearance();
// phones: keep the composer above the on-screen keyboard (VisualViewport → --app-height / --kb-inset)
installViewportVars();
// desktop shell (Phase 12): native notifications, tray, deep links, shortcuts, picker… — a chunk of its own that a
// browser never downloads
if (IS_DESKTOP) void import('./lib/desktop').then(m => m.installDesktop(router));
// iOS app: Keychain-backed session, moods:// links, status bar colour — likewise a chunk only the app loads
if (IS_IOS_APP) void import('./lib/ios').then(m => m.installIos(router));
void useAuth.getState().bootstrap();
// the screen being opened (and, with a likely session, the shell) start loading while the session refresh runs
preloadRoute(location.pathname === '/' ? '/home' : location.pathname, useAuth.getState().status !== 'anonymous');
// PWA (Phase 11): production web builds only — not the dev server, not the desktop / iOS app (app://, updates with the app)
registerServiceWorker();
// after the first screen had its turn: one /config/public request, then (only if on) the Sentry chunk
setTimeout(() => void installMonitoring(), 3000);

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');
// first render once `common` + `shell` (and the UI kit copy) of the current locale are there — bundled for Turkish,
// so only English waits for its small chunks
void Promise.all([i18nReady, loadUiLocale(i18n.language === 'en' ? 'en' : 'tr').catch(() => undefined)]).then(() =>
  createRoot(root).render(
    <StrictMode>
      <Providers>
        {IS_DESKTOP ? (
          <Suspense fallback={null}>
            <DesktopFrame />
          </Suspense>
        ) : null}
        <RouterProvider router={router} />
      </Providers>
    </StrictMode>,
  ),
);
